import React, { useEffect, useState, useRef, useCallback } from "react";
import "../styles/AdminPublications.css";
import { BOOKS_API, API, API_BASE } from "../config/api";

const resolveImageUrl = (imgUrl) => {
  if (!imgUrl) return "";
  let str = String(imgUrl).trim();
  if (str.startsWith("data:")) return str;

  let finalUrl = "";
  if (str.includes("/uploads/")) {
    const filename = str.split("/uploads/")[1];
    finalUrl = `${API_BASE}uploads/${filename}`;
  } else if (str.startsWith("uploads/")) {
    const filename = str.slice("uploads/".length);
    finalUrl = `${API_BASE}uploads/${filename}`;
  } else if (str.startsWith("http://") || str.startsWith("https://")) {
    if (str.includes("localhost:") || str.includes("127.0.0.1:")) {
      const match = str.match(/\/uploads\/(.+)$/);
      if (match) {
        finalUrl = `${API_BASE}uploads/${match[1]}`;
      } else {
        finalUrl = str;
      }
    } else {
      finalUrl = str;
    }
  } else {
    finalUrl = `${API_BASE}${str.replace(/^\//, "")}`;
  }

  return encodeURI(finalUrl);
};

const AdminPublications = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("all-books");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [editingBookId, setEditingBookId] = useState(null);
  const [expandedSets, setExpandedSets] = useState({});

  // Confirmation Modal State
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    subMessage: "",
    confirmLabel: "Yes, Delete",
    cancelLabel: "Cancel",
    onConfirm: null,
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && confirmDialog.isOpen) {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [confirmDialog.isOpen]);

  const toggleSet = (bookId) => {
    setExpandedSets((prev) => ({
      ...prev,
      [bookId]: !prev[bookId],
    }));
  };

  const isSetExpanded = (bookId) => {
    if (expandedSets[bookId]) return true;
    if (searchQuery.trim()) {
      const children = books.filter((b) => b.parent_book_id === bookId);
      return children.some(
        (child) =>
          (child.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           child.author.toLowerCase().includes(searchQuery.toLowerCase())) &&
          (!filterCategory || child.category === filterCategory)
      );
    }
    return false;
  };

  const getFilteredChildren = (parentBookId) => {
    const children = books.filter((b) => b.parent_book_id === parentBookId);
    if (!searchQuery && !filterCategory) return children;
    return children.filter(
      (child) =>
        (child.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
         child.author.toLowerCase().includes(searchQuery.toLowerCase())) &&
        (!filterCategory || child.category === filterCategory)
    );
  };

  const popupRef = useRef(null);

  // ================= POPUP =================
  const showPopup = (msg, type = "success") => {
    if (popupRef.current) popupRef.current.remove();

    const div = document.createElement("div");
    popupRef.current = div;

    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${type === "error" ? "#dc2626" : "#5d1d2b"};
        color: #fff;
        padding: 14px 18px;
        border-radius: 10px;
        z-index: 99999;
        box-shadow: 0 10px 25px rgba(93, 29, 43, 0.25);
        font-size: 14px;
        font-weight: 600;
        max-width: 300px;
        animation: slideIn 0.3s ease;
      ">
        ${msg}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>
    `;

    document.body.appendChild(div);

    setTimeout(() => {
      div.remove();
      popupRef.current = null;
    }, 2500);
  };

  const [newBook, setNewBook] = useState({
    title: "",
    author: "",
    price: "",
    category: "",
    description: "",
    image: "",
    is_full_book: false,
    parent_book_id: "",
    weight_grams: 250,
  });

  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null); // { id, name }
  const [editCategoryName, setEditCategoryName] = useState("");



  // ================= LOAD CATEGORIES =================
  const loadCategories = useCallback(async () => {
    try {
      setCategoryLoading(true);
      const res = await fetch(`${BOOKS_API}?action=getCategories`);
      const data = await res.json();
      if (data.status === "success") {
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error("Error loading categories:", err);
      showPopup("Error loading categories", "error");
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  // ================= LOAD BOOKS =================
  const loadBooks = useCallback(async () => {
    try {
      const res = await fetch(`${BOOKS_API}?action=getBooks`);
      const data = await res.json();
      setBooks(data.books || []);
      showPopup("Books loaded successfully");
    } catch (err) {
      console.error(err);
      showPopup("Error loading books", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
    loadCategories();
  }, [loadBooks, loadCategories]);

  // ================= CATEGORY ACTIONS =================
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      showPopup("Category name cannot be empty", "error");
      return;
    }
    try {
      const res = await fetch(`${BOOKS_API}?action=addCategory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newCategoryName }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Category added successfully");
        setNewCategoryName("");
        loadCategories();
      } else {
        showPopup(data.message || "Failed to add category", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server error", "error");
    }
  };

  const handleEditCategory = async () => {
    if (!editCategoryName.trim()) {
      showPopup("Category name cannot be empty", "error");
      return;
    }
    try {
      const res = await fetch(`${BOOKS_API}?action=editCategory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: editingCategory.id, name: editCategoryName }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Category updated successfully");
        setEditingCategory(null);
        setEditCategoryName("");
        loadCategories();
        loadBooks(); // Reload books as categories changed
      } else {
        showPopup(data.message || "Failed to update category", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server error", "error");
    }
  };

  const confirmDeleteCategory = (cat) => {
    const targetCat = typeof cat === "object" ? cat : categories.find((c) => c.id === cat) || { id: cat };
    setConfirmDialog({
      isOpen: true,
      title: "Delete Catalog Folder",
      message: "Are you sure you want to delete this category?",
      subMessage: targetCat?.name
        ? `"${targetCat.name}" will be deleted. Any books in this category will become uncategorized.`
        : "Any books in this category will be uncategorized.",
      confirmLabel: "Delete Category",
      cancelLabel: "Cancel",
      onConfirm: () => executeDeleteCategory(targetCat.id),
    });
  };

  const executeDeleteCategory = async (catId) => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    try {
      const res = await fetch(`${BOOKS_API}?action=deleteCategory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: catId }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Category deleted successfully");
        loadCategories();
        loadBooks(); // Reload books as categories changed
      } else {
        showPopup(data.message || "Failed to delete category", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server error", "error");
    }
  };

  // ================= FILE UPLOAD =================
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API}?action=upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.status === "success") {
        setNewBook((prev) => ({ ...prev, image: data.url }));
        showPopup("Image uploaded");
      } else {
        showPopup("Upload failed", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Upload error", "error");
    } finally {
      setUploading(false);
    }
  };

  // ================= ADD / EDIT BOOK =================
  const addBook = async () => {
    if (
      !newBook.title ||
      !newBook.author ||
      !newBook.price ||
      !newBook.category
    ) {
      showPopup("Fill all required fields", "error");
      return;
    }

    try {
      setActionLoading(true);
      const action = editingBookId ? "editBook" : "addBook";
      const payload = editingBookId ? { ...newBook, id: editingBookId } : newBook;

      const res = await fetch(`${BOOKS_API}?action=${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === "success") {
        setNewBook({
          title: "",
          author: "",
          price: "",
          category: "",
          description: "",
          image: "",
          is_full_book: false,
          parent_book_id: "",
          weight_grams: 250,
        });
        setEditingBookId(null);
        loadBooks();
        showPopup(editingBookId ? "Book updated successfully" : "Book added successfully");
        setActiveTab("all-books");
      } else {
        showPopup(data.message || "Operation failed", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server error", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const startEditBook = (book) => {
    setEditingBookId(book.id);
    setNewBook({
      title: book.title || "",
      author: book.author || "",
      price: book.price || "",
      category: book.category || "",
      description: book.description || "",
      image: book.image || "",
      is_full_book: Number(book.is_full_book) === 1,
      parent_book_id: book.parent_book_id || "",
      weight_grams: book.weight_grams || 250,
    });
    setActiveTab("add-book");
  };

  const cancelEdit = () => {
    setEditingBookId(null);
    setNewBook({
      title: "",
      author: "",
      price: "",
      category: "",
      description: "",
      image: "",
      is_full_book: false,
      parent_book_id: "",
      weight_grams: 250,
    });
    setActiveTab("all-books");
  };

  // ================= DELETE BOOK =================
  const confirmDeleteBook = (book) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Book",
      message: "Are you sure you want to delete this book?",
      subMessage: book?.title
        ? `"${book.title}" will be permanently removed from your catalog.`
        : "This action cannot be undone.",
      confirmLabel: "Delete Book",
      cancelLabel: "Cancel",
      onConfirm: () => executeDeleteBook(book.id),
    });
  };

  const confirmDeletePart = (part) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Part Book",
      message: "Are you sure you want to delete this book?",
      subMessage: part?.title
        ? `"${part.title}" will be removed from this set.`
        : "This action cannot be undone.",
      confirmLabel: "Delete Part",
      cancelLabel: "Cancel",
      onConfirm: () => executeDeleteBook(part.id),
    });
  };

  const executeDeleteBook = async (id) => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    try {
      setActionLoading(true);
      await fetch(`${BOOKS_API}?action=deleteBook&id=${id}`);
      loadBooks();
      showPopup("Book deleted successfully");
    } catch (err) {
      console.error(err);
      showPopup("Delete failed", "error");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="publications-loading">
        <div className="spinner-border text-primary" role="status"></div>
        <p>Loading catalog...</p>
      </div>
    );
  }

  // Filtered Books calculation
  const filteredBooks = books.filter((book) => {
    // Only display top-level books (standalone or full book sets)
    if (book.parent_book_id) return false;

    const parentMatches =
      (book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
       book.author.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (!filterCategory || book.category === filterCategory);

    if (Number(book.is_full_book) === 1) {
      // Check if any of its children match
      const children = books.filter((b) => b.parent_book_id === book.id);
      const anyChildMatches = children.some(
        (child) =>
          (child.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           child.author.toLowerCase().includes(searchQuery.toLowerCase())) &&
          (!filterCategory || child.category === filterCategory)
      );
      return parentMatches || anyChildMatches;
    }

    return parentMatches;
  });

  return (
    <div className="admin-container publications-admin-container">
      
      {/* Header with Dashboard Metrics */}
      <div className="publications-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-journal-bookmark-fill"></i> Publications Manager
          </h1>
          <p>Organize, categorize, and publish books and sets in the digital store catalog</p>
        </div>

        <div className="publications-metrics-grid">
          <div className="metric-card">
            <div className="metric-icon books"><i className="bi bi-book"></i></div>
            <div className="metric-info">
              <span className="metric-value">{books.length}</span>
              <span className="metric-label">Total Books</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon categories"><i className="bi bi-tags"></i></div>
            <div className="metric-info">
              <span className="metric-value">{categories.length}</span>
              <span className="metric-label">Categories</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon combos"><i className="bi bi-box-seam"></i></div>
            <div className="metric-info">
              <span className="metric-value">{books.filter(b => Number(b.is_full_book) === 1).length}</span>
              <span className="metric-label">Full Sets / Combos</span>
            </div>
          </div>
        </div>
      </div>

      {/* PAGE TABS NAVIGATION */}
      <div className="publications-tabs">
        <button
          type="button"
          className={`pub-tab-btn ${activeTab === "all-books" ? "active" : ""}`}
          onClick={() => setActiveTab("all-books")}
        >
          <i className="bi bi-grid-3x3-gap"></i>
          <span className="tab-text-container">
            <strong>Books Catalog</strong>
            <span className="tab-subtext">View and manage books</span>
          </span>
          <span className="badge-count">{books.length}</span>
        </button>
        
        <button
          type="button"
          className={`pub-tab-btn ${activeTab === "add-book" ? "active" : ""}`}
          onClick={() => setActiveTab("add-book")}
        >
          <i className="bi bi-plus-circle"></i>
          <span className="tab-text-container">
            <strong>Add New Book</strong>
            <span className="tab-subtext">Publish new item</span>
          </span>
        </button>

        <button
          type="button"
          className={`pub-tab-btn ${activeTab === "categories" ? "active" : ""}`}
          onClick={() => setActiveTab("categories")}
        >
          <i className="bi bi-folder2-open"></i>
          <span className="tab-text-container">
            <strong>Manage Categories</strong>
            <span className="tab-subtext">Edit folder labels</span>
          </span>
          <span className="badge-count">{categories.length}</span>
        </button>
      </div>

      {/* TAB CONTENT LAYOUTS */}
      
      {/* ================= BOOKS CATALOG TAB ================= */}
      {activeTab === "all-books" && (
        <div className="tab-pane active-fade">
          <div className="catalog-toolbar">
            <div className="search-bar-wrapper">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="admin-search-input"
                style={{ paddingLeft: "48px" }}
                placeholder="Search by title or author..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={() => setSearchQuery("")}>
                  <i className="bi bi-x-circle-fill"></i>
                </button>
              )}
            </div>
            
            <div className="filter-dropdown-wrapper">
              <i className="bi bi-funnel"></i>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="publications-catalog-section">
            {filteredBooks.length === 0 ? (
              <div className="empty-catalog-state">
                <i className="bi bi-journal-x"></i>
                <h3>No books found</h3>
                <p>Try refining your search query or selected category filter.</p>
              </div>
            ) : (
              <div className="admin-grid">
                {filteredBooks.map((book) => (
                  <div key={book.id} className="admin-card book-card">
                    <div className="book-card-image-container">
                      <img
                        src={resolveImageUrl(book.image)}
                        alt={book.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='240' viewBox='0 0 200 240'%3E%3Crect width='200' height='240' fill='%23f1f5f9'/%3E%3Ctext x='100' y='110' font-size='40' text-anchor='middle' dominant-baseline='middle'%3E📚%3C/text%3E%3Ctext x='100' y='150' font-size='12' text-anchor='middle' fill='%2394a3b8'%3ENo Cover Image%3C/text%3E%3C/svg%3E";
                        }}
                      />
                      <span className="price-tag">₹{Number(book.price).toFixed(2)}</span>
                    </div>

                    <div className="book-card-body">
                      <h3>{book.title}</h3>
                      <p className="author">by {book.author}</p>
                      
                      <div className="book-badges-row">
                        <span className="cat-badge">{book.category}</span>
                        <span className="weight-badge" style={{ background: "#fef3c7", color: "#92400e", padding: "3px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <i className="bi bi-box-seam"></i> {book.weight_grams || 250}g
                        </span>
                        {Number(book.is_full_book) === 1 && (
                          <span className="type-badge">
                            <i className="bi bi-box-seam"></i> Full Book Set
                          </span>
                        )}
                        {book.parent_book_id && (
                          <span className="parent-badge" title={books.find(b => b.id === book.parent_book_id)?.title || "Full Book"}>
                            <i className="bi bi-link-45deg"></i> Part of Set
                          </span>
                        )}
                      </div>
                      
                      <p className="desc">{book.description || "No synopsis available."}</p>

                      {Number(book.is_full_book) === 1 && (
                        <button
                          type="button"
                          className={`toggle-parts-btn ${isSetExpanded(book.id) ? "expanded" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSet(book.id);
                          }}
                        >
                          <i className={`bi ${isSetExpanded(book.id) ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                          {isSetExpanded(book.id) ? "Hide Set Books" : `View Set Books (${books.filter(b => b.parent_book_id === book.id).length})`}
                        </button>
                      )}

                      {Number(book.is_full_book) === 1 && isSetExpanded(book.id) && (
                        <div className="set-parts-section" onClick={(e) => e.stopPropagation()}>
                          <div className="set-parts-header">
                            <h4>Books in this Set ({getFilteredChildren(book.id).length})</h4>
                          </div>
                          <div className="set-parts-list">
                            {getFilteredChildren(book.id).length === 0 ? (
                              <p className="no-parts-found">No matching set books found.</p>
                            ) : (
                              getFilteredChildren(book.id).map((part) => (
                                <div key={part.id} className="set-part-row">
                                  <div className="part-thumbnail-wrapper">
                                    <img
                                      src={resolveImageUrl(part.image)}
                                      alt={part.title}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='50' viewBox='0 0 40 50'%3E%3Crect width='40' height='50' fill='%23f1f5f9'/%3E%3Ctext x='20' y='25' font-size='16' text-anchor='middle' dominant-baseline='middle'%3E📚%3C/text%3E%3C/svg%3E";
                                      }}
                                    />
                                  </div>
                                  <div className="part-info">
                                    <span className="part-title">{part.title}</span>
                                    <span className="part-price">₹{Number(part.price).toFixed(2)} • ⚖️ {part.weight_grams || 250}g</span>
                                  </div>
                                  <div className="part-actions">
                                    <button
                                      type="button"
                                      className="part-action-btn edit"
                                      title="Edit Part"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        startEditBook(part);
                                      }}
                                    >
                                      <i className="bi bi-pencil-square"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="part-action-btn delete"
                                      title="Delete Part"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        confirmDeletePart(part);
                                      }}
                                    >
                                      <i className="bi bi-trash3"></i>
                                    </button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="book-card-actions">
                      <button
                        className="edit-btn"
                        onClick={() => startEditBook(book)}
                      >
                        <i className="bi bi-pencil-square"></i> Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => confirmDeleteBook(book)}
                      >
                        <i className="bi bi-trash3"></i> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= ADD BOOK TAB ================= */}
      {activeTab === "add-book" && (
        <div className="tab-pane active-fade">
          <div className="admin-card book-form-card-v2">
            
            {/* Header info */}
            <div className="form-header-area">
              <h2 className="card-title">
                <i className={editingBookId ? "bi bi-pencil-square" : "bi bi-plus-circle-fill"}></i> 
                {editingBookId ? "Edit Book Details" : "Publish New Book"}
              </h2>
              <p className="form-subtitle">Enter details below to publish a new book or set in the catalog, or edit an existing one.</p>
              {editingBookId && (
                <div className="edit-indicator-badge">
                  <i className="bi bi-info-circle-fill"></i> You are currently editing book ID: #{editingBookId}
                </div>
              )}
            </div>

            {/* Steps Progress Tracker */}
            <div className="publishing-progress-tracker">
              <div className={`progress-step ${newBook.title && newBook.author ? "completed" : "active"}`}>
                <span className="step-num">{newBook.title && newBook.author ? <i className="bi bi-check-lg"></i> : "1"}</span>
                <span className="step-label">Core Details</span>
              </div>
              <div className="step-connector"></div>
              <div className={`progress-step ${newBook.category && newBook.price ? "completed" : (newBook.title && newBook.author ? "active" : "")}`}>
                <span className="step-num">{newBook.category && newBook.price ? <i className="bi bi-check-lg"></i> : "2"}</span>
                <span className="step-label">Pricing & Category</span>
              </div>
              <div className="step-connector"></div>
              <div className={`progress-step ${newBook.image && newBook.description ? "completed" : (newBook.category && newBook.price ? "active" : "")}`}>
                <span className="step-num">{newBook.image && newBook.description ? <i className="bi bi-check-lg"></i> : "3"}</span>
                <span className="step-label">Artwork & Synopsis</span>
              </div>
            </div>

            <div className="form-sections-grid">
              
              {/* Left Column: Form Fields */}
              <div className="form-fields-column">
                
                {/* Panel 1: Core Details */}
                <div className="form-panel-card">
                  <h3 className="panel-title">
                    <i className="bi bi-card-text"></i> Core Information
                  </h3>
                  <div className="form-group-row">
                    <div className="form-group-v2">
                      <label>Book Title *</label>
                      <div className="input-with-icon">
                        <i className="bi bi-book"></i>
                        <input
                          type="text"
                          placeholder="Enter book title"
                          value={newBook.title}
                          onChange={(e) => setNewBook({ ...newBook, title: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group-v2">
                      <label>Author Name *</label>
                      <div className="input-with-icon">
                        <i className="bi bi-person"></i>
                        <input
                          type="text"
                          placeholder="Enter author name"
                          value={newBook.author}
                          onChange={(e) => setNewBook({ ...newBook, author: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Panel 2: Pricing & Classification */}
                <div className="form-panel-card">
                  <h3 className="panel-title">
                    <i className="bi bi-tags"></i> Pricing & Classification
                  </h3>
                  <div className="form-group-row">
                    <div className="form-group-v2">
                      <label>Price (₹) *</label>
                      <div className="input-with-icon">
                        <span className="currency-symbol">₹</span>
                        <input
                          type="text"
                          placeholder="e.g. 150"
                          value={newBook.price}
                          onChange={(e) => setNewBook({ ...newBook, price: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group-v2">
                      <label>Category *</label>
                      <div className="input-with-icon">
                        <i className="bi bi-tag"></i>
                        <select
                          value={newBook.category}
                          onChange={(e) => setNewBook({ ...newBook, category: e.target.value })}
                          required
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.name}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-group-v2">
                      <label>Weight (Grams) *</label>
                      <div className="input-with-icon">
                        <i className="bi bi-box-seam"></i>
                        <input
                          type="number"
                          placeholder="e.g. 250"
                          value={newBook.weight_grams || 250}
                          onChange={(e) => setNewBook({ ...newBook, weight_grams: e.target.value })}
                          min="1"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Panel 3: Hierarchy & Set Settings */}
                <div className="form-panel-card">
                  <h3 className="panel-title">
                    <i className="bi bi-diagram-3"></i> Book Type & Hierarchy
                  </h3>
                  <p className="panel-subtitle" style={{ fontSize: "13px", color: "#64748b", marginTop: "-16px", marginBottom: "18px" }}>
                    Choose whether this item is a single standalone book, a full combo set, or a part of a set.
                  </p>
                  
                  <div className="book-type-selector-grid">
                    {/* Option 1: Standalone Book */}
                    <div 
                      className={`book-type-option-card ${!newBook.is_full_book && !newBook.parent_book_id ? "selected" : ""}`}
                      onClick={() => setNewBook(prev => ({ ...prev, is_full_book: false, parent_book_id: "" }))}
                    >
                      <div className="option-radio-circle">
                        {!newBook.is_full_book && !newBook.parent_book_id && <div className="radio-inner-dot"></div>}
                      </div>
                      <div className="option-icon-wrap standalone">
                        <i className="bi bi-book"></i>
                      </div>
                      <div className="option-text-wrap">
                        <strong>Single / Standalone Book</strong>
                        <span>Individual book sold separately (Default)</span>
                      </div>
                    </div>

                    {/* Option 2: Full Book Set / Combo */}
                    <div 
                      className={`book-type-option-card ${newBook.is_full_book ? "selected" : ""}`}
                      onClick={() => setNewBook(prev => ({ ...prev, is_full_book: true, parent_book_id: "" }))}
                    >
                      <div className="option-radio-circle">
                        {newBook.is_full_book && <div className="radio-inner-dot"></div>}
                      </div>
                      <div className="option-icon-wrap set">
                        <i className="bi bi-box-seam"></i>
                      </div>
                      <div className="option-text-wrap">
                        <strong>Full Book Set / Combo Pack</strong>
                        <span>Parent bundle containing multiple volumes</span>
                      </div>
                    </div>

                    {/* Option 3: Part of a Book Set */}
                    <div 
                      className={`book-type-option-card ${!newBook.is_full_book && newBook.parent_book_id ? "selected" : ""}`}
                      onClick={() => {
                        const firstParent = books.find(b => Number(b.is_full_book) === 1);
                        setNewBook(prev => ({ 
                          ...prev, 
                          is_full_book: false, 
                          parent_book_id: prev.parent_book_id || (firstParent ? String(firstParent.id) : "") 
                        }));
                      }}
                    >
                      <div className="option-radio-circle">
                        {!newBook.is_full_book && newBook.parent_book_id && <div className="radio-inner-dot"></div>}
                      </div>
                      <div className="option-icon-wrap part">
                        <i className="bi bi-diagram-2"></i>
                      </div>
                      <div className="option-text-wrap">
                        <strong>Part of a Book Set</strong>
                        <span>Volume belonging to an existing parent set</span>
                      </div>
                    </div>
                  </div>

                  {/* If Part of a Set is selected, show parent dropdown */}
                  {!newBook.is_full_book && newBook.parent_book_id !== "" && (
                    <div className="form-group-v2 select-parent-group animation-slide-down" style={{ marginTop: "18px" }}>
                      <label>Select Parent Book Set *</label>
                      <div className="input-with-icon">
                        <i className="bi bi-box-seam"></i>
                        <select
                          value={newBook.parent_book_id}
                          onChange={(e) => setNewBook({ ...newBook, parent_book_id: e.target.value })}
                          required
                        >
                          <option value="">-- Choose Parent Book Set --</option>
                          {books
                            .filter((b) => Number(b.is_full_book) === 1 && b.id !== editingBookId)
                            .map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.title} (ID: #{b.id})
                              </option>
                            ))}
                        </select>
                      </div>
                      {books.filter(b => Number(b.is_full_book) === 1).length === 0 && (
                        <p style={{ fontSize: "12.5px", marginTop: "8px", color: "#d97706", display: "flex", alignItems: "center", gap: "6px" }}>
                          <i className="bi bi-info-circle"></i> No Full Book Sets exist yet. Create a "Full Book Set" first to attach part books to it.
                        </p>
                      )}
                    </div>
                  )}
                </div>

              </div>

              {/* Right Column: Media and Synopsis */}
              <div className="form-media-column">
                
                {/* Panel 4: Cover & Synopsis */}
                <div className="form-panel-card h-100">
                  <h3 className="panel-title">
                    <i className="bi bi-image"></i> Cover Art & Synopsis
                  </h3>
                  
                  <div className="form-group-v2">
                    <label>Book Description / Synopsis</label>
                    <textarea
                      placeholder="Provide a brief synopsis, description, or contents of the book..."
                      value={newBook.description}
                      onChange={(e) => setNewBook({ ...newBook, description: e.target.value })}
                      rows="6"
                    />
                  </div>

                  <div className="form-group-v2" style={{ marginTop: "15px" }}>
                    <label>Cover Image</label>
                    <div className="upload-dropzone-v2">
                      <i className="bi bi-cloud-arrow-up"></i>
                      <p>{uploading ? "Uploading cover..." : "Drag & drop or click to select book cover"}</p>
                      <input type="file" onChange={handleFileUpload} accept="image/*" />
                    </div>
                  </div>

                  {newBook.image && (
                    <div className="book-preview-card-wrapper-v2">
                      <div className="book-3d-scene">
                        <div className="book-3d-mockup">
                          <img src={resolveImageUrl(newBook.image)} alt="Preview" />
                        </div>
                      </div>
                      <div className="upload-preview-info-v2">
                        <i className="bi bi-check-circle-fill text-success"></i>
                        <span>Mockup Generated! File: {newBook.image.split("/").pop()}</span>
                      </div>
                    </div>
                  )}
                </div>

              </div>

            </div>

            {/* Form Actions */}
            <div className="form-actions-row-v2">
              {editingBookId && (
                <button type="button" className="btn-cancel-action-v2" onClick={cancelEdit}>
                  <i className="bi bi-x-circle"></i> Cancel Edit
                </button>
              )}
              <button className="btn-submit-action-v2" onClick={addBook} disabled={actionLoading}>
                <i className={editingBookId ? "bi bi-check-circle-fill" : "bi bi-plus-circle-fill"}></i> 
                {actionLoading ? "Processing..." : editingBookId ? "Save Book Details" : "Publish Book"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ================= MANAGE CATEGORIES TAB ================= */}
      {activeTab === "categories" && (
        <div className="tab-pane active-fade categories-pane-redesign">
          <div className="categories-split-layout">
            
            {/* Left: Create Category Form Card */}
            <div className="admin-card create-category-card">
              <div className="card-decor-icon">
                <i className="bi bi-folder-plus"></i>
              </div>
              <h2 className="card-title">Create Category</h2>
              <p className="card-subtitle-desc">Define a new category folder to classify books in your store catalog</p>
              
              <div className="form-group" style={{ marginTop: "20px" }}>
                <label>Category Folder Name</label>
                <div className="category-input-wrapper">
                  <input
                    type="text"
                    placeholder="e.g. Yajur Veda"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                  />
                </div>
              </div>
              
              <button className="btn-add-category-large" onClick={handleAddCategory}>
                <i className="bi bi-plus-circle-fill"></i> Add Category
              </button>
            </div>

            {/* Right: Existing Categories list */}
            <div className="admin-card categories-list-card">
              <h2 className="card-title">
                <i className="bi bi-collection-fill"></i> Catalog Folders
              </h2>
              <p className="card-subtitle-desc">Browse, rename, or remove existing folders</p>
              
              {categoryLoading ? (
                <div className="category-loading">
                  <div className="spinner-border text-primary" role="status" style={{ color: "#5d1d2b" }}></div>
                  <p>Loading catalog folders...</p>
                </div>
              ) : categories.length === 0 ? (
                <div className="empty-categories-state">
                  <i className="bi bi-tags"></i>
                  <h3>No category folders created yet</h3>
                  <p>Use the creation panel on the left to add your first folder.</p>
                </div>
              ) : (
                <div className="category-list-container">
                  {categories.map((cat) => {
                    const bookCount = books.filter(b => b.category === cat.name).length;
                    const isEditing = editingCategory?.id === cat.id;

                    return (
                      <div key={cat.id} className={`category-row-item-redesign ${isEditing ? "editing" : ""}`}>
                        {isEditing ? (
                          <div className="category-edit-wrapper-redesign">
                            <label>Rename Folder</label>
                            <input
                              type="text"
                              className="category-rename-input"
                              value={editCategoryName}
                              onChange={(e) => setEditCategoryName(e.target.value)}
                            />
                            <div className="category-edit-actions">
                              <button className="btn-edit-action save" onClick={handleEditCategory}>
                                <i className="bi bi-check2"></i> Save
                              </button>
                              <button className="btn-edit-action cancel" onClick={() => setEditingCategory(null)}>
                                <i className="bi bi-x-lg"></i> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="folder-icon-wrapper">
                              <i className="bi bi-folder-fill"></i>
                            </div>
                            <div className="folder-details">
                              <span className="folder-title" title={cat.name}>
                                {cat.name}
                              </span>
                              <span className="folder-subtitle">
                                {bookCount} {bookCount === 1 ? 'item' : 'items'}
                              </span>
                            </div>
                            <div className="folder-actions-overlay">
                              <button
                                className="folder-action-icon-btn edit"
                                title="Rename Folder"
                                onClick={() => {
                                  setEditingCategory(cat);
                                  setEditCategoryName(cat.name);
                                }}
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </button>
                              <button
                                className="folder-action-icon-btn delete"
                                title="Delete Folder"
                                onClick={() => confirmDeleteCategory(cat)}
                              >
                                <i className="bi bi-trash-fill"></i>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* CUSTOM CONFIRMATION MODAL */}
      {confirmDialog.isOpen && (
        <div
          className="admin-confirm-modal-overlay"
          onClick={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
        >
          <div
            className="admin-confirm-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="admin-confirm-close-btn"
              onClick={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
              title="Close"
            >
              <i className="bi bi-x-lg"></i>
            </button>

            <div className="admin-confirm-icon-bubble">
              <i className="bi bi-trash3-fill"></i>
            </div>

            <h3 className="admin-confirm-title">{confirmDialog.title}</h3>

            <p className="admin-confirm-message">{confirmDialog.message}</p>

            {confirmDialog.subMessage && (
              <div className="admin-confirm-detail-box">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <span>{confirmDialog.subMessage}</span>
              </div>
            )}

            <div className="admin-confirm-actions">
              <button
                type="button"
                className="admin-confirm-btn-cancel"
                onClick={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
              >
                {confirmDialog.cancelLabel || "Cancel"}
              </button>
              <button
                type="button"
                className="admin-confirm-btn-danger"
                onClick={() => {
                  if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                }}
              >
                <i className="bi bi-trash3"></i>
                {confirmDialog.confirmLabel || "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPublications;
